import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, Phone, Mail, Instagram, MessageCircle, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { CONTACT, WHATSAPP_MSG, SERVICES } from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ nome: "", email: "", telefone: "", condominio: "", servico: "", mensagem: "" });
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.email || !form.telefone || !form.mensagem) {
      toast.error("Preencha os campos obrigatórios.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Mensagem enviada! Entraremos em contato em breve.");
      setForm({ nome: "", email: "", telefone: "", condominio: "", servico: "", mensagem: "" });
    } catch (err) {
      toast.error("Não foi possível enviar. Tente pelo WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 focus:border-coral outline-none py-3 text-white placeholder:text-muted-fg/60 transition-colors duration-300";

  return (
    <section id="contato" data-testid="contact-section" className="relative py-28 sm:py-36 blueprint-grid">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16">
        {/* Left: info */}
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-coral">/ Fale conosco</span>
          <h2 className="mt-5 font-display font-extrabold tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            Agende uma<br />visita técnica.
          </h2>
          <p className="mt-6 text-muted-fg text-lg leading-relaxed max-w-md">
            Nossa equipe está pronta para avaliar suas instalações e apresentar soluções
            adequadas à realidade do seu condomínio.
          </p>

          <div className="mt-12 space-y-5">
            <a href={`https://wa.me/${CONTACT.phoneRaw}?text=${WHATSAPP_MSG}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="group flex items-center gap-4 text-white hover:text-coral transition-colors duration-300">
              <span className="grid place-items-center w-11 h-11 border border-white/15 group-hover:border-coral transition-colors duration-300"><MessageCircle size={18} /></span>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-fg">WhatsApp</div>
                <div className="font-semibold">{CONTACT.phone}</div>
              </div>
            </a>
            <a href={`tel:+${CONTACT.phoneRaw}`} data-testid="contact-phone" className="group flex items-center gap-4 text-white hover:text-coral transition-colors duration-300">
              <span className="grid place-items-center w-11 h-11 border border-white/15 group-hover:border-coral transition-colors duration-300"><Phone size={18} /></span>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-fg">Telefone</div>
                <div className="font-semibold">{CONTACT.phone}</div>
              </div>
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="group flex items-center gap-4 text-white hover:text-coral transition-colors duration-300">
              <span className="grid place-items-center w-11 h-11 border border-white/15 group-hover:border-coral transition-colors duration-300"><Mail size={18} /></span>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-fg">E-mail</div>
                <div className="font-semibold break-all">{CONTACT.email}</div>
              </div>
            </a>
            <a href={CONTACT.instagramUrl} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="group flex items-center gap-4 text-white hover:text-coral transition-colors duration-300">
              <span className="grid place-items-center w-11 h-11 border border-white/15 group-hover:border-coral transition-colors duration-300"><Instagram size={18} /></span>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-fg">Instagram</div>
                <div className="font-semibold">@{CONTACT.instagram}</div>
              </div>
            </a>
          </div>
        </Reveal>

        {/* Right: form */}
        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid="contact-form" className="glass border border-white/10 p-8 sm:p-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="sm:col-span-2">
                <label className="font-mono text-[10px] uppercase tracking-widest text-muted-fg">Nome *</label>
                <input data-testid="input-nome" value={form.nome} onChange={upd("nome")} className={inputCls} placeholder="Seu nome" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-muted-fg">E-mail *</label>
                <input data-testid="input-email" type="email" value={form.email} onChange={upd("email")} className={inputCls} placeholder="seu@email.com" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-muted-fg">Telefone *</label>
                <input data-testid="input-telefone" value={form.telefone} onChange={upd("telefone")} className={inputCls} placeholder="(13) 90000-0000" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-muted-fg">Condomínio</label>
                <input data-testid="input-condominio" value={form.condominio} onChange={upd("condominio")} className={inputCls} placeholder="Nome do condomínio" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-muted-fg block mb-1">Serviço</label>
                <Select value={form.servico} onValueChange={(v) => setForm((f) => ({ ...f, servico: v }))}>
                  <SelectTrigger data-testid="select-servico" className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 text-white focus:ring-0 focus:border-coral">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-white/10 text-white">
                    {SERVICES.map((s) => (
                      <SelectItem key={s.title} value={s.title} className="focus:bg-coral focus:text-white">{s.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono text-[10px] uppercase tracking-widest text-muted-fg">Mensagem *</label>
                <textarea data-testid="input-mensagem" value={form.mensagem} onChange={upd("mensagem")} rows={3} className={`${inputCls} resize-none`} placeholder="Como podemos ajudar?" />
              </div>
            </div>
            <motion.button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              whileTap={{ scale: 0.98 }}
              className="group mt-8 w-full inline-flex items-center justify-center gap-3 bg-coral hover:bg-coral-hover text-white font-semibold px-7 py-4 transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? <Loader2 size={20} className="animate-spin" /> : <>Enviar mensagem <ArrowUpRight size={20} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" /></>}
            </motion.button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
